// ============================================================
// serverWake — shared backend/relay URL resolution + silent cold-start wake-up
// ============================================================
//
// Free-tier hosting (Render/Fly-style) sleeps the backend (:4000) and relay
// (:4001) after ~15 min of inactivity. Waking a sleeping instance costs 30-60s.
// `warmServers()` fires throwaway health pings the moment the app boots on ANY
// route (index.js), so the servers spin up *while the user reads* instead of
// stalling them at login. The backend also wakes the relay itself whenever it is
// hit (backend/src/lib/relay-wake.js), as a second path to the same result.
//
// This module is also the single source of truth for BACKEND_URL / RELAY_URL —
// App.jsx and AuthContext.jsx import BACKEND_URL from here rather than each
// pasting their own copy of the resolver.

// Resolve a configured URL's hostname to match the current page hostname.
// If the user visits via "localhost:3000" but the env var points at
// "127.0.0.1:4000", Chrome treats them as cross-site and refuses to send
// SameSite=Lax cookies — breaking session auth on reload and Socket.io auth.
// Rewriting the hostname to window.location.hostname keeps requests same-site.
function resolveOrigin(configured, fallback) {
  const raw = configured || fallback;
  if (!raw) return '';
  try {
    const url = new URL(raw);
    if (url.hostname === '127.0.0.1' || url.hostname === 'localhost') {
      // window may be undefined in non-browser contexts (tests/SSR) — guard it.
      if (typeof window !== 'undefined' && window.location) {
        url.hostname = window.location.hostname;
      }
    }
    return url.origin;
  } catch {
    return raw;
  }
}

// The relay is reached over WebSocket, so its URL is often configured as
// ws(s)://. fetch() only speaks http(s), so map the scheme for the health ping.
function toHttpOrigin(origin) {
  return origin.replace(/^ws(s?):\/\//, 'http$1://');
}

// Hosted relay used by the published extension. When REACT_APP_RELAY_URL isn't
// set, the project's own production hosts fall back to it so real visits still
// wake the relay. Any other host (self-hosted builds, LAN/dev hosts) keeps the
// documented meaning of empty = no relay ping.
const HOSTED_RELAY_URL = 'https://debugger-relay.onrender.com';
const HOSTED_SITE_HOSTS = ['interceptapi.in', 'www.interceptapi.in', 'debugger-frontend.netlify.app'];

function defaultRelayUrl() {
  if (typeof window === 'undefined' || !window.location) return '';
  return HOSTED_SITE_HOSTS.includes(window.location.hostname) ? HOSTED_RELAY_URL : '';
}

// Backend always has a sensible localhost fallback.
export const BACKEND_URL = resolveOrigin(
  process.env.REACT_APP_BACKEND_URL,
  'http://127.0.0.1:4000'
);
export const RELAY_URL = toHttpOrigin(resolveOrigin(process.env.REACT_APP_RELAY_URL, defaultRelayUrl()));

// Fire-and-forget wake-up of the free-tier servers.
//   - keepalive: true   → the GET survives an immediate page navigation
//                         (e.g. the user clicks "Get Started" right away), so
//                         the wake isn't cancelled mid-flight.
//   - cache: 'no-store'  → never satisfied by a cached 200; always hits the
//                         origin, which is what actually triggers spin-up.
//   - .catch(() => {})   → errors are irrelevant; we only want the request to
//                         reach the platform router. No UI, no logging, silent.
//   - run-once guard     → one wake per page load is enough; avoids re-pinging
//                         on re-render or when multiple pages mount in sequence.
let warmed = false;
let lastWarmAt = 0;

export function warmServers({ force = false } = {}) {
  if (typeof fetch === 'undefined') return; // non-browser guard (tests/SSR)
  if (warmed && !force) return;
  warmed = true;
  lastWarmAt = Date.now();

  fetch(`${BACKEND_URL}/api/health`, { keepalive: true, cache: 'no-store' }).catch(() => {});

  if (RELAY_URL) {
    // no-cors: the relay's /health sends no CORS headers, and we never read the
    // response — reaching Render's router is what triggers the spin-up.
    fetch(`${RELAY_URL}/health`, { keepalive: true, cache: 'no-store', mode: 'no-cors' }).catch(() => {});
  }
}

// Free-tier servers fall asleep again after ~15 min idle. If the visitor leaves
// the tab in the background that long and comes back, wake them again right away
// rather than on their next click. Called once from index.js.
const REWARM_AFTER_MS = 10 * 60 * 1000;

export function installWakeOnReturn() {
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - lastWarmAt > REWARM_AFTER_MS) {
      warmServers({ force: true });
    }
  });
}

// Poll the backend health endpoint until it responds OK or the deadline passes.
// Used to wait out a free-tier cold start (Render sleeps the instance after
// ~15 min; waking it takes 30-60s). Returns true once the backend is awake,
// false if it never came up within timeoutMs.
export async function waitForBackend({ timeoutMs = 75000, intervalMs = 2500 } = {}) {
  if (typeof fetch === 'undefined') return false;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const res = await fetch(`${BACKEND_URL}/api/health`, { cache: 'no-store' });
      if (res.ok) return true;
    } catch { /* still asleep — keep polling */ }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return false;
}
